import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  onSnapshot,
  orderBy,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from "firebase/functions";
import { formatAge } from '../utils/dateHelpers';
import { processingFeeDollars } from '../utils/fees';
import { app, auth, db } from '../firebaseConfig';
import InfoTip from './InfoTip';

// One-line explanations shown next to each application status pill
const STATUS_TIPS_PARENT = {
  'Pending': 'Waiting for you to accept or deny this applicant.',
  'Awaiting Sitter Confirmation': 'You accepted — the sitter has 24 hours to confirm before the offer expires.',
  'Active': 'Booked! You can message your sitter from this card.',
  'Completed': 'Your sitter submitted hours — review and pay. If you don’t, payment auto-processes after 24 hours.',
  'Paid': 'Payment sent to your sitter. You can leave a review.',
};

const functions = getFunctions(app);
const processPayment = httpsCallable(functions, "processPayment");
const cancelJob = httpsCallable(functions, "cancelJob");
const deleteJobFn = httpsCallable(functions, "adminDeleteJob"); 

console.log("Functions initialized:", functions);

const Chat = ({ applicationId, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sitterDetails, setSitterDetails] = useState(null);

  // Navigation uses window.location.href for reliability

  useEffect(() => {
    // Fetch sitter details
    const fetchSitterDetails = async () => {
      try {
        const applicationDoc = await getDoc(doc(db, 'applications', applicationId));
        if (applicationDoc.exists()) {
                  const applicationData = applicationDoc.data();
        const sitterDoc = await getDoc(doc(db, 'users', applicationData.sitterId));
        if (sitterDoc.exists()) {
          const sitterData = sitterDoc.data();
          sitterData.uid = sitterDoc.id; // Add the uid field
          setSitterDetails(sitterData);
        }
        }
      } catch (error) {
        console.error('Error fetching sitter details:', error);
      }
    };

    fetchSitterDetails();

    const messagesQuery = query(
      collection(db, 'messages'),
      where('applicationId', '==', applicationId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      setMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const sendMessage = async () => {
    if (!newMessage.trim()) {
      console.error('Message is empty.');
      return;
    }

    if (!sitterDetails?.uid) {
      console.error('Sitter details not loaded yet. sitterDetails:', sitterDetails);
      alert('Sitter details not loaded yet. Please try again.');
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        alert('User not authenticated');
        window.location.href = '/auth';
        return;
      }

      const messageData = {
        applicationId,
        senderId: user.uid,
        recipientId: sitterDetails.uid,
        message: newMessage,
        timestamp: new Date(),
      };

      await addDoc(collection(db, 'messages'), messageData);

      setNewMessage(''); // Clear the input after sending
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please check your connection and try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center px-4 z-50">
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-lg relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl text-gray-500 hover:text-gray-700 focus:outline-none"
          aria-label="Close chat"
        >
          &times;
        </button>
  
        {/* Sitter Info */}
        {sitterDetails && (
          <div className="flex items-center mb-4 mt-2">
            <img
              src={sitterDetails.profilePictureURL || 'https://via.placeholder.com/50'}
              alt="Sitter Profile"
              className="w-12 h-12 rounded-full mr-3 border"
            />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
              {sitterDetails.name || 'Sitter'}
            </h2>
          </div>
        )}
  
        {/* Messages */}
        <div className="chat-container max-h-64 overflow-y-auto mb-4 p-3 sm:p-4 pt-6 bg-gray-100 rounded-lg space-y-2">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${
                msg.senderId === auth.currentUser.uid ? 'justify-end' : 'justify-start'
              } group`}
            >
              <div
                className={`relative max-w-[80%] sm:max-w-xs p-2 text-sm rounded-lg ${
                  msg.senderId === auth.currentUser.uid
                    ? 'bg-blue-500 text-white rounded-br-none'
                    : 'bg-gray-200 text-gray-800 rounded-bl-none'
                }`}
              >
                {msg.message}
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 text-xs bg-gray-800 text-white px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 shadow-lg border border-gray-600 pointer-events-none">
                  {new Date(msg.timestamp?.toDate()).toLocaleString()}
                  {/* Arrow pointing down */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
  
        {/* Message Input */}
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-grow p-3 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <button
            onClick={sendMessage}
            className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
  
};

const ReviewModal = ({ sitterId, onClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const handleRatingClick = (index) => {
    setRating(index + 1);
  };

  const handleSubmit = async () => {
    onSubmit({ rating, comment });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-bold">Review Sitter</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              &times;
            </button>
          </div>
          <div className="flex justify-center mb-4">
            {[...Array(5)].map((_, index) => (
              <span
                key={index}
                className={`text-3xl cursor-pointer ${
                  index < rating ? 'text-yellow-400' : 'text-gray-300'
                }`}
                onClick={() => handleRatingClick(index)}
              >
                ★
              </span>
            ))}
          </div>
          <textarea
            className="w-full p-2 border rounded mb-4"
            placeholder="Leave a comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows="3"
          />
        </div>
        <div className="p-6 pt-0 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentModal = ({ application, onClose, onConfirmPayment, userData }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const [customTip, setCustomTip] = useState('');
  const [selectedTipPreset, setSelectedTipPreset] = useState(0);

  const platformFeePercentage = 0.10; // 10% platform fee
  // Processing fee comes from processingFeeDollars (break-even with Stripe's costs)
  // Coerce to numbers with zero-fallback so a missing hours/rate produces $0
  // instead of NaN (which used to reach Stripe as an invalid amount).
  const hoursWorkedNum = Number(application.totalHoursWorked) || 0;
  const jobPayRateNum = Number(application.jobPayRange) || 0;
  const sitterPayment = hoursWorkedNum * jobPayRateNum; // SP
  const platformFee = sitterPayment * platformFeePercentage; // 10% of SP
  const subtotalBeforeProcessing = sitterPayment + platformFee;
  // Break-even processing fee (mirrors backend; depends on tip since the card
  // fee applies to the tip portion of the charge too)
  const processingFee = processingFeeDollars(sitterPayment, tipAmount);
  // Final amount = subtotal before processing + processing fee + tip (tip excluded from fee calculations)
  const finalAmount = subtotalBeforeProcessing + processingFee + tipAmount;

  const tipPresets = [10, 15, 20];

  const handleTipPreset = (percentage) => {
    setSelectedTipPreset(percentage);
    setTipAmount(Math.round(sitterPayment * (percentage / 100) * 100) / 100);
    setCustomTip('');
  };

  const handleNoTip = () => {
    setSelectedTipPreset(0);
    setTipAmount(0);
    setCustomTip('');
  };

  const handleCustomTip = (value) => {
    setSelectedTipPreset('custom');
    const parsed = parseFloat(value);
    setCustomTip(value);
    setTipAmount(isNaN(parsed) || parsed < 0 ? 0 : Math.min(parsed, 500));
  };

  const handlePayClick = async () => {
    if (isProcessing) return; // Prevent double-clicks
    if (!sitterPayment || sitterPayment <= 0) {
      alert('This job has no hours logged — nothing to pay. Please refresh and try again.');
      return;
    }
    setIsProcessing(true);
    try {
      await onConfirmPayment(finalAmount.toFixed(2), sitterPayment.toFixed(2), platformFee.toFixed(2), processingFee.toFixed(2), tipAmount);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-start sm:items-center overflow-y-auto z-50 py-4 px-4" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
      <div className="bg-white/95 backdrop-blur-sm border border-white/40 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100 my-auto">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Confirm Payment</h3>
                <p className="text-sm text-slate-600">Complete your job payment</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isProcessing}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${
                isProcessing 
                  ? 'bg-gray-100 cursor-not-allowed' 
                  : 'bg-slate-100 hover:bg-slate-200'
              }`}
            >
              <svg className={`w-4 h-4 ${isProcessing ? 'text-gray-400' : 'text-slate-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Job Summary */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
            <h4 className="text-sm font-semibold text-blue-800 uppercase tracking-wide mb-3">Job Summary</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Hours Worked:</span>
                <span className="font-semibold text-slate-800">{application.totalHoursWorked}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Hourly Rate:</span>
                <span className="font-semibold text-slate-800">${application.jobPayRange}</span>
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Sitter Payment:</span>
              <span className="font-semibold text-slate-800">${sitterPayment.toFixed(2)}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <span className="text-slate-600">Platform Fee (10%):</span>
              <span className="font-semibold text-amber-700">${platformFee.toFixed(2)}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <span className="text-slate-600">Processing Fee:</span>
              <span className="font-semibold text-orange-700">${processingFee.toFixed(2)}</span>
            </div>
          </div>

          {/* Tip Section */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl">
            <h4 className="text-sm font-semibold text-purple-800 uppercase tracking-wide mb-3">Add a Tip <InfoTip text="100% of the tip goes to your sitter — no fees are taken out of it." /></h4>
            <p className="text-xs text-purple-600 mb-3">100% goes to your sitter — no fees deducted</p>
            <div className="flex gap-2 mb-3">
              {tipPresets.map((pct) => (
                <button
                  key={pct}
                  onClick={() => handleTipPreset(pct)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    selectedTipPreset === pct
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-white text-purple-700 border border-purple-200 hover:bg-purple-100'
                  }`}
                >
                  {pct}%
                </button>
              ))}
              <button
                onClick={() => { setSelectedTipPreset('custom'); setCustomTip(''); setTipAmount(0); }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  selectedTipPreset === 'custom'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-white text-purple-700 border border-purple-200 hover:bg-purple-100'
                }`}
              >
                Custom
              </button>
            </div>
            {selectedTipPreset === 'custom' && (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-600 font-semibold">$</span>
                <input
                  type="number"
                  min="0"
                  max="500"
                  step="0.01"
                  value={customTip}
                  onChange={(e) => handleCustomTip(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full pl-7 pr-3 py-2 border border-purple-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
            )}
            {tipAmount > 0 && (
              <div className="flex items-center justify-between mt-3 p-3 bg-white/60 rounded-lg">
                <span className="text-purple-700">Tip (100% to sitter):</span>
                <span className="font-semibold text-purple-800">${tipAmount.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Total Amount */}
          <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-emerald-800">Final Amount:</span>
              <span className="text-2xl font-bold text-emerald-800">${finalAmount.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Method */}
          <div className="mb-6 p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-600">Payment Method</p>
                <p className="font-semibold text-slate-800">Card ending in ...{userData.cardLast4}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pb-2">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className={`flex-1 px-6 py-4 font-semibold rounded-xl transition-all duration-200 border ${
                isProcessing 
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
                  : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handlePayClick}
              disabled={isProcessing}
              className={`flex-1 px-6 py-4 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg ${
                isProcessing 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 transform hover:scale-105 shadow-emerald-500/25'
              }`}
            >
              <span className="flex items-center justify-center space-x-2">
                {isProcessing ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    <span>Pay Now</span>
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ParentJobs = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Listed Jobs');
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [modalType, setModalType] = useState(null); // "chat" or "confirm"
  const [activeApplicationId, setActiveApplicationId] = useState(null);
  const [listedJobs, setListedJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [activeSitterId, setActiveSitterId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [paymentApplication, setPaymentApplication] = useState(null);
  const [isTimesModalOpen, setIsTimesModalOpen] = useState(false);
  const [selectedJobForTimes, setSelectedJobForTimes] = useState(null);
  const [selectedApplicationForTimes, setSelectedApplicationForTimes] = useState(null);
  const [toConfirmCount, setToConfirmCount] = useState(0);
  const [appCount, setAppCount] = useState(0);
  const [cancellingIds, setCancellingIds] = useState(() => new Set()); // guards against double cancel

  // Navigation uses window.location.href for reliability

  const orderDays = (days) => {
    const orderedDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const ordered = {};
    orderedDays.forEach((day) => {
      if (days && days[day] !== undefined) {
        ordered[day] = days[day];
      }
    });
    return ordered;
  };

  function formatLocalDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00'); // Prevent timezone offset
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });
  }


  

  const fetchApplications = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert('You need to be logged in to view applications.');
        window.location.href = '/auth';
        return;
      }

      // Fetch user role
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        alert('User data not found.');
        return;
      }
      const userData = userDoc.data();
      if (userData.userType !== 'parent') {
        alert('Only parents can access this page.');
        window.location.href = '/auth'; // Redirect to auth for unauthorized access
        return;
      }

      setCurrentUser(userData);

      const jobsQuery = query(collection(db, 'jobs'), where('parentId', '==', user.uid));
      const jobsSnapshot = await getDocs(jobsQuery);
      const jobIds = jobsSnapshot.docs.map((doc) => doc.id);

      if (jobIds.length === 0) {
        setApplications([]);
        setLoading(false);
        return;
      }

      // const applicationsQuery = query(
      //   collection(db, 'applications'),
      //   where('jobId', 'in', jobIds),
      //   orderBy('appliedAt', 'desc')
      // );
      // const applicationsSnapshot = await getDocs(applicationsQuery);
      // const applicationsList = await Promise.all(
      //   applicationsSnapshot.docs.map(async (document) => {
      //     const application = { id: document.id, ...document.data() };

      //     // Fetch sitter's profile picture from the users table
      //     const sitterDoc = await getDoc(doc(db, 'users', application.sitterId));
      //     if (sitterDoc.exists()) {
      //       application.sitterProfilePictureURL = sitterDoc.data().profilePictureURL || '';
      //     }

      //     application.jobDays = orderDays(application.jobDays);

      //     return application;
      //   })
      // );

      // Firestore allows a maximum of 30 values in an "in" query
      // Must include parentId filter to satisfy Firestore security rules
      const applicationPromises = [];
      for (let i = 0; i < jobIds.length; i += 30) {
        const jobIdBatch = jobIds.slice(i, i + 30);
        const applicationsQuery = query(
          collection(db, 'applications'),
          where('parentId', '==', user.uid),
          where('jobId', 'in', jobIdBatch),
          orderBy('appliedAt', 'desc')
        );
        applicationPromises.push(getDocs(applicationsQuery));
      }

      let confirmCount = 0;
      let applicationCount = 0;

      const applicationSnapshots = await Promise.all(applicationPromises);
      const applicationsList = [];
      const sitterIds = new Set();
      
      // First pass: collect all sitter IDs
      for (const snapshot of applicationSnapshots) {
        for (const document of snapshot.docs) {
          const application = { id: document.id, ...document.data() };
          sitterIds.add(application.sitterId);
        }
      }
      
      // Batch fetch all sitter profile pictures at once
      const sitterProfileMap = new Map();
      const sitterIdArray = Array.from(sitterIds);
      for (let i = 0; i < sitterIdArray.length; i += 30) {
        const batch = sitterIdArray.slice(i, i + 30);
        const sitterPromises = batch.map(sitterId => getDoc(doc(db, 'users', sitterId)));
        const sitterDocs = await Promise.all(sitterPromises);
        sitterDocs.forEach((sitterDoc, index) => {
          if (sitterDoc.exists()) {
            sitterProfileMap.set(batch[index], sitterDoc.data().profilePictureURL || '');
          }
        });
      }
      
      // Second pass: build applications list with profile pictures
      for (const snapshot of applicationSnapshots) {
        for (const document of snapshot.docs) {
          const application = { id: document.id, ...document.data() };

          confirmCount += application.status === 'Completed' ? 1 : 0;
          applicationCount +=  (application.status === 'Pending' || application.status === 'Awaiting Sitter Confirmation') ? 1 : 0;

          // Get sitter's profile picture from the map
          application.sitterProfilePictureURL = sitterProfileMap.get(application.sitterId) || '';

          applicationsList.push(application);
        }
      }
      setToConfirmCount(confirmCount);
      setAppCount(applicationCount);
      console.log("Confirm count:", confirmCount);
      console.log("App count:", appCount);

      setApplications(applicationsList);
      handleFilterChange(filter);
      console.log("fetch set")
      
    } catch (error) {
      console.error('Error fetching applications: ', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleFilterChange(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, filter]);

  // Fetch jobs posted by the parent
  const fetchJobsPostedByUser = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert('You need to be logged in.');
        window.location.href = '/auth';
        return [];
      }

      // Fetch user role
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        alert('User data not found.');
        return;
      }
      const userData = userDoc.data();
      if (userData.userType !== 'parent') {
        alert('Only parents can access this page.');
        window.location.href = '/auth'; // Redirect to auth for unauthorized access
        return;
      }

      setCurrentUser(userData);

      const jobsQuery = query(collection(db, 'jobs'), where('parentId', '==', user.uid), where('status', '==', 'Open'));
      const jobsSnapshot = await getDocs(jobsQuery);
      setLoading(false);
      return jobsSnapshot.docs.map((doc) => {
        const job = { id: doc.id, ...doc.data() };
        job.days = orderDays(job.days); // Order days before setting
        return job;
      });
    } catch (error) {
      console.error('Error fetching jobs:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = async (status) => {
    setFilter(status);
  
    let sortedApplications = [];
    switch (status) {
      case 'Job Applications':
        sortedApplications = applications
          .filter((application) => application.status === 'Pending' || application.status === 'Awaiting Sitter Confirmation')
          .sort((a, b) => new Date(b.appliedAt.seconds * 1000) - new Date(a.appliedAt.seconds * 1000));
        break;
      case 'Active Jobs':
        sortedApplications = applications
          .filter((application) => application.status === 'Active')
          .sort((a, b) => new Date(a.jobStartDate) - new Date(b.jobStartDate));
        break;
      case 'Hours To Confirm':
        sortedApplications = applications
          .filter((application) => application.status === 'Completed')
          .sort((a, b) => new Date(b.jobEndDate) - new Date(a.jobEndDate));
        break;
      case 'Completed Jobs':
        sortedApplications = applications
          .filter((application) => application.status === "Paid")
          .sort((a, b) => new Date(b.jobEndDate) - new Date(a.jobEndDate));
        break;
      case 'Listed Jobs': {
        const jobs = await fetchJobsPostedByUser();
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const activeJobs = jobs.filter((job) => {
          if (!job.endDate) return true;
          return new Date(job.endDate + 'T23:59:59') >= todayStart;
        });
        setListedJobs(activeJobs);
        setFilteredApplications([]);
        break;
      }
      case 'Expired Listings': {
        const jobs = await fetchJobsPostedByUser();
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const expiredJobs = jobs.filter((job) => {
          if (!job.endDate) return false;
          return new Date(job.endDate + 'T23:59:59') < todayStart;
        });
        setListedJobs(expiredJobs);
        setFilteredApplications([]);
        break;
      }
      default:
        sortedApplications = applications;
    }
    
    setFilteredApplications(sortedApplications);

    console.log("filteredApplications", filteredApplications.length);
  };
  

  const handleApplicationAction = async (applicationId, action) => {
    try {
      const applicationRef = doc(db, 'applications', applicationId);
      const applicationSnapshot = await getDoc(applicationRef);
  
      if (applicationSnapshot.exists()) {
        const applicationData = applicationSnapshot.data();
  
        if (action === 'accept') {
          // 🔐 Check if parent has a payment method
          const parentRef = doc(db, 'users', applicationData.parentId);
          const parentSnap = await getDoc(parentRef);
  
          if (!parentSnap.exists()) {
            alert('Parent data not found.');
            return;
          }
  
          const parentData = parentSnap.data();
  
          if (!parentData.paymentMethodId) {
            alert('Please add a payment method before accepting applications.');
            return;
          }
        }
  
        // ✅ Update application status (offerSentAt lets the admin panel show
        // when the offer went out and when the 24h window expires)
        await updateDoc(applicationRef, {
          status: action === 'accept' ? 'Awaiting Sitter Confirmation' : 'Family Denied',
          ...(action === 'accept' ? { offerSentAt: new Date() } : {}),
        });
  
        // Update local state
        setApplications((prevApplications) =>
          prevApplications.map((application) =>
            application.id === applicationId
              ? { ...application, status: action === 'accept' ? 'Awaiting Sitter Confirmation' : 'Family Denied' }
              : application
          )
        );
  
        alert(action === 'accept'
          ? 'Application accepted successfully. Awaiting confirmation from the sitter.'
          : 'Application denied successfully.'
        );
      } else {
        alert('Application not found.');
      }
  
      await fetchApplications();
      handleFilterChange(filter);
  
    } catch (error) {
      console.error(`Error ${action}ing application: `, error);
      alert(`Failed to ${action} application. Please try again.`);
    }
  };

  const handleConfirmJob = async (application) => {
    try {
      console.log('Confirming job, showing payment modal...');
      setPaymentApplication(application);
      setActiveApplicationId(application.id);
      setActiveSitterId(application.sitterId);
      // Show payment modal instead of review modal
      // The review modal will only appear after successful payment

      handleFilterChange(filter);

    } catch (error) {
      console.error('Error confirming job:', error);
      alert('Failed to confirm job. Please try again.');
    }
  };

  const handleSubmitReview = async (review) => {
    try {
      if (!activeSitterId) return;
      const rating = Number(review?.rating) || 0;
      const comment = (review?.comment || '').trim();
      // Reviews are optional — if nothing was entered, just close the modal without saving
      if (rating === 0 && comment === '') {
        setModalType(null);
        setActiveSitterId(null);
        return;
      }
      const reviewData = {
        subjectId: activeSitterId,
        reviewerId: auth.currentUser?.uid,
        reviewerProfilePicture: currentUser.profilePictureURL,
        ...review,
        comment,
        createdAt: new Date(),
        reviewerName: currentUser.name
      };

      await addDoc(collection(db, 'reviews'), reviewData);

      alert('Review submitted successfully!');
      setModalType(null);
      setActiveSitterId(null);
      setFilter('Completed Jobs');
      fetchApplications();
      handleFilterChange('Completed Jobs');
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    }
  };

  const handleCancelJob = async (application) => {
    if (!application || !application.id) return;
    if (cancellingIds.has(application.id)) return; // hard guard against double-click
    // Check if cancellation is within 24 hours of job start
    let feeMessage = "";
    if (application.jobStartDate && application.jobStartTime) {
      const jobStartDate = new Date(`${application.jobStartDate} ${application.jobStartTime}`);
      const now = new Date();
      const hoursUntilStart = (jobStartDate - now) / (1000 * 60 * 60);
      
      if (hoursUntilStart <= 24 && hoursUntilStart >= 0) {
        const cancellationFee = application.cancellationFee || 25;
        feeMessage = ` A $${cancellationFee} cancellation fee will be charged.`;
      } else {
        feeMessage = " No fee will be charged as cancellation is more than 24 hours before job start.";
      }
    } else {
      // Default to showing fee if we can't determine job start time
      const cancellationFee = application.cancellationFee || 25;
      feeMessage = ` A $${cancellationFee} cancellation fee will be charged.`;
    }
    
    const confirmCancel = window.confirm(`Are you sure you want to cancel this job?${feeMessage}`);
  
    if (!confirmCancel) return;

    setCancellingIds(prev => new Set(prev).add(application.id));
    try {
      const response = await cancelJob({
        userId: auth.currentUser.uid,
        applicationId: application.id,
      });

      console.log("Cancel job response:", response.data);

      if (!response.data) {
        throw new Error("No response data received");
      }

      if (response.data.success) {
        const feeCharged = response.data.cancellationFee || 0;
        if (feeCharged > 0) {
          alert(`Job cancelled successfully. A $${feeCharged} cancellation fee has been charged.`);
        } else {
          alert(`Job cancelled successfully. No fee was charged as cancellation was more than 24 hours before job start.`);
        }
        await fetchApplications(filter);
        handleFilterChange(filter);
      } else {
        let errorMessage = response.data.message || "Cancellation failed. Please try again.";
        
        // Provide more helpful error message for customer-related errors
        if (errorMessage.includes("No such customer") || errorMessage.includes("customer")) {
          errorMessage = "There was an issue with your payment account. Please contact support or try again in a few minutes. The system is updating your payment information.";
        }
        
        alert(errorMessage);
        console.error('Cancellation failed:', response.data);
      }
    } catch (error) {
      console.error("Cancellation error:", error);
      const errorMessage = error.message || "An error occurred while cancelling the job. Please try again.";
      alert(errorMessage);
    } finally {
      setCancellingIds(prev => {
        const next = new Set(prev);
        next.delete(application.id);
        return next;
      });
    }
  };

  const handlePaymentSuccess = async (amountPaid, sitterPayout, platformFee, processingFee, tipAmount = 0) => {
    try {
      // CRITICAL: Check if application is already paid before processing payment
      const applicationDoc = await getDoc(doc(db, 'applications', paymentApplication.id));
      if (applicationDoc.exists() && applicationDoc.data().status === 'Paid') {
        alert('This job has already been paid. Please refresh the page.');
        setPaymentApplication(null);
        fetchApplications();
        return;
      }

      const getParentPaymentMethod = async (parentId) => {
        const parentRef = doc(db, 'users', parentId);
        const parentDoc = await getDoc(parentRef);
      
        if (parentDoc.exists()) {
          return parentDoc.data().paymentMethodId; // This is the stored card token
        } else {
          throw new Error('Parent profile not found');
        }
      };

      try {
        const parentPaymentMethod = await getParentPaymentMethod(paymentApplication.parentId);
    
        if (!parentPaymentMethod) {
          alert("No payment method found. Please add a card on file.");
          return;
        }
    
        // Call backend function to process the payment
        // IMPORTANT: Pass sitterPayout (base amount), NOT amountPaid (total with fees)
        // Backend calculates fees itself to ensure consistency
        const { parentId, sitterId } = paymentApplication;

        const response = await processPayment({
          parentId: parentId,
          sitterId: sitterId,
          amount: sitterPayout, // Base sitter payment - backend adds fees
          tipAmount: tipAmount, // Optional tip - 100% goes to sitter, no fees
          application: paymentApplication,
        });

        console.log('Payment status:', response.status);

        // Handle "already paid" response from backend
        if (response.data.alreadyPaid) {
          alert('This job has already been paid. Please refresh the page.');
          setPaymentApplication(null);
          fetchApplications();
          return;
        }

        if (response.data.success) {
          await updateDoc(doc(db, 'applications', paymentApplication.id), { status: 'Paid' });
          setApplications(prev => prev.map(app => app.id === paymentApplication.id ? { ...app, status: 'Paid', amountPaid } : app));
          setPaymentApplication(null);
          alert('Payment successful! Job confirmed.');
          console.log('Payment successful, now showing review modal...');
          setModalType("review");
          setFilter('Completed Jobs');
          fetchApplications();
          handleFilterChange('Completed Jobs');
        } else {
          alert('Payment failed: ' + (response.data.message || 'Please try again.'));
        }
      } catch (error) {
        console.error('Error confirming job:', error);
        alert('Payment failed: ' + (error.message || 'Please try again.'));
      }


      // await updateDoc(applicationRef, { status: 'Confirmed', amountPaid });


  
      // setApplications(prev => prev.map(app => app.id === paymentApplication.id ? { ...app, status: 'Confirmed', amountPaid } : app));
      // setPaymentApplication(null);
      // alert('Payment Successful! Job Confirmed.');
    } catch (error) {
      console.error('Error confirming job:', error);
      alert('Payment failed. Please try again.');
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job? Any pending applicants will be emailed that the listing was removed, and all related applications and messages will be deleted. This cannot be undone.')) {
      return;
    }
    try {
      // Server-side delete: verifies ownership, blocks deleting a booked job,
      // emails pending applicants, and cleans up applications + messages.
      const res = await deleteJobFn({ jobId });
      if (res.data?.success) {
        alert(`Job deleted${res.data.emailsSent ? ` — ${res.data.emailsSent} applicant(s) notified` : ''}.`);
        const jobs = await fetchJobsPostedByUser();
        setListedJobs(jobs);
        fetchApplications();
      } else {
        alert('Delete failed: ' + (res.data?.message || 'Please try again.'));
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      // Surface the friendly "use Cancel instead" message for booked jobs.
      alert(error.message || 'Failed to delete job. Please try again.');
    }
  };
  
  
  const handleModalOpen = (type, applicationId) => {
    setModalType(type)
    setActiveApplicationId(applicationId);
  };

  const handleModalClose = () => {
    setModalType(null);
    setActiveApplicationId(null);
  };

  const formatTimeToAMPM = (time24) => {
    if (!time24) return '';
    const [hourStr, minute] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  };

  

  const JobTimesModal = ({ isOpen, onClose, job }) => {
    // Handle escape key - must be called before early return
    useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === 'Escape' && isOpen) {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
      }
    }, [isOpen, onClose]);

    if (!isOpen || !job?.schedule) return null;

    const handleOverlayClick = (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    const handleModalClick = (e) => {
      e.stopPropagation();
    };
    
    return (
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50"
        onClick={handleOverlayClick}
      >
        <div 
          className="bg-white/95 backdrop-blur-sm border border-white/40 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col transform transition-all duration-300 scale-100"
          onClick={handleModalClick}
        >
          {/* Header */}
          <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Job Schedule</h2>
                  <p className="text-sm text-slate-600">{job.title}</p>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors duration-200"
              >
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            {job.schedule && Object.keys(job.schedule).length > 0 ? (
              <div className="space-y-4">
                {Object.keys(job.schedule)
                  .sort((a, b) => new Date(a) - new Date(b)) // Sort dates chronologically
                  .map((date) => {
                    const ranges = Array.isArray(job.schedule[date])
                      ? job.schedule[date]
                      : [job.schedule[date]];
    
                    return (
                      <div key={date} className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <h3 className="font-semibold text-slate-800">{formatLocalDate(date)}</h3>
                        </div>
                        
                        <div className="space-y-2">
                          {ranges.map((range, index) => (
                            <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                              <div className="flex items-center space-x-3">
                                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <span className="text-slate-700 font-medium">
                                  {formatTimeToAMPM(range.start)} - {formatTimeToAMPM(range.end)}
                                </span>
                              </div>
                              <div className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full">
                                Range {index + 1}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-slate-500 font-medium">No days scheduled</p>
                <p className="text-sm text-slate-400">This job doesn't have a schedule set yet.</p>
              </div>
            )}

          </div>

          {/* Sticky footer */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-slate-200/50 flex justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="px-6 py-3 bg-gradient-to-r from-slate-500 to-slate-600 text-white font-semibold rounded-xl hover:from-slate-600 hover:to-slate-700 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-emerald-500/25"
            >
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Close</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ApplicationTimesModal = ({ isOpen, onClose, application }) => {
    if (!isOpen || !application?.jobSchedule) return null;

    const handleOverlayClick = (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    return (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50"
        onClick={handleOverlayClick}
      >
        <div
          className="bg-white/95 backdrop-blur-sm border border-white/40 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col transform transition-all duration-300 scale-100"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Application Schedule</h2>
                  <p className="text-sm text-slate-600">{application.jobTitle}</p>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors duration-200"
              >
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            {application.jobSchedule && Object.keys(application.jobSchedule).length > 0 ? (
              <div className="space-y-4">
                {Object.keys(application.jobSchedule)
                  .sort((a, b) => new Date(a) - new Date(b)) // Sort dates chronologically
                  .map((date) => {
                    const ranges = Array.isArray(application.jobSchedule[date])
                      ? application.jobSchedule[date]
                      : [application.jobSchedule[date]];
    
                    return (
                      <div key={date} className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <h3 className="font-semibold text-slate-800">{formatLocalDate(date)}</h3>
                        </div>
                        
                        <div className="space-y-2">
                          {ranges.map((range, index) => (
                            <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                              <div className="flex items-center space-x-3">
                                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <span className="text-slate-700 font-medium">
                                  {formatTimeToAMPM(range.start)} - {formatTimeToAMPM(range.end)}
                                </span>
                              </div>
                              <div className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full">
                                Range {index + 1}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-slate-500 font-medium">No days scheduled</p>
                <p className="text-sm text-slate-400">This application doesn't have a schedule set yet.</p>
              </div>
            )}

          </div>

          {/* Sticky footer */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-slate-200/50 flex justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="px-6 py-3 bg-gradient-to-r from-slate-500 to-slate-600 text-white font-semibold rounded-xl hover:from-slate-600 hover:to-slate-700 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-slate-500/25"
            >
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Close</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const JobEditModal = ({ job, onClose, onSave }) => {
    const [formData, setFormData] = useState(job);
    const [childrenAges, setChildrenAges] = useState(job.childrenAges || []);
    const [numChildren, setNumChildren] = useState(job.numChildren || 0);
    const [selectedDates, setSelectedDates] = useState(job.selectedDates || []);

  
    const handleInputChange = (key, value) => {
      setFormData({ ...formData, [key]: value });
    };
  
    const handleDayChange = (day, time) => {
      setFormData({
        ...formData,
        days: { ...formData.days, [day]: time },
      });
    };

    const generateDateRange = (start, end) => {
      // Parse dates without timezone issues by creating dates in local timezone
      const startDate = new Date(start + 'T00:00:00');
      const endDate = new Date(end + 'T00:00:00');
      const dates = [];
    
      while (startDate <= endDate) {
        // Format date as YYYY-MM-DD without timezone conversion
        const year = startDate.getFullYear();
        const month = String(startDate.getMonth() + 1).padStart(2, '0');
        const day = String(startDate.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
        startDate.setDate(startDate.getDate() + 1);
      }
    
      return dates;
    };

    const getEffectiveDates = () => {
      // If selectedDates exist, use them; otherwise fall back to generated date range
      if (selectedDates && selectedDates.length > 0) {
        return selectedDates;
      }
      if (formData.startDate && formData.endDate) {
        return generateDateRange(formData.startDate, formData.endDate);
      }
      return [];
    };

    useEffect(() => {
      if (formData.startDate && formData.endDate) {
        const dates = generateDateRange(formData.startDate, formData.endDate);
        if (!dates.length) return;
    
        const updatedSchedule = { ...formData.schedule };
    
        dates.forEach((date) => {
          if (!updatedSchedule[date]) {
            // Only assign a default if the date is missing
            updatedSchedule[date] = { start: '', end: '' };
          }
        });
    
        setFormData((prev) => ({
          ...prev,
          schedule: updatedSchedule,
        }));

        // Initialize selectedDates if they don't exist
        if (!selectedDates || selectedDates.length === 0) {
          setSelectedDates(dates);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.startDate, formData.endDate, formData.schedule, selectedDates]);
    
  
    const handleSave = async () => {
      // Get today's date in YYYY-MM-DD format without timezone issues
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const currentDate = `${year}-${month}-${day}`;

      // Check required fields
      if (!formData.title.trim()) {
        alert('Job title is required.');
        return;
      }
      if (!formData.description.trim()) {
        alert('Job description is required.');
        return;
      }
      if (!formData.payRange || isNaN(Number(formData.payRange)) || Number(formData.payRange) < 22) {
        alert('Minimum pay rate is $22/hr.');
        return;
      }
      if (!formData.numChildren || formData.numChildren <= 0) {
        alert('Number of children must be greater than 0.');
        return;
      }
      if (!formData.startDate) {
        alert('Start date is required.');
        return;
      }
      if (formData.startDate < currentDate) {
        alert('Start date must be on or after today.');
        return;
      }

      if (formData.startDate === currentDate && formData.jobStartTime) {
        // Compare 24h formData.jobStartTime ("14:30") with current 24h time (HH:MM),
        // not with toLocaleTimeString() ("2:30:00 PM") which is a lexicographic mismatch.
        const nowHHMM = `${String(today.getHours()).padStart(2, '0')}:${String(today.getMinutes()).padStart(2, '0')}`;
        if (formData.jobStartTime <= nowHHMM) {
          alert('Start time must be in the future.');
          return;
        }
      }
      if (!formData.endDate) {
        alert('End date is required.');
        return;
      }
      if (formData.endDate < formData.startDate) {
        alert('End date must be on or after the start date.');
        return;
      }

      // Validate selected dates
      const effectiveDates = getEffectiveDates();
      if (effectiveDates.length === 0) {
        alert('Please select at least one date for the job.');
        return;
      }

      // Check maximum job length (1 week = 7 days)
      if (effectiveDates.length > 7) {
        alert('Job cannot exceed one week (7 days). Please select fewer dates.');
        return;
      }

      const dateKeys = effectiveDates;

      for (let date of dateKeys) {
        const timeRanges = Array.isArray(formData.schedule[date])
          ? formData.schedule[date]
          : [formData.schedule[date]];

        for (let i = 0; i < timeRanges.length; i++) {
          const { start, end } = timeRanges[i];
          if (!start || !end) {
            alert(`Please enter both start and end times for ${date} (range ${i + 1})`);
            return;
          }

          const [startH, startM] = start.split(":").map(Number);
          const [endH, endM] = end.split(":").map(Number);
          const startTime = startH * 60 + startM;
          const endTime = endH * 60 + endM;

          if (startTime >= endTime) {
            alert(`Start time must be before end time on ${date} (range ${i + 1}).\n\nFor overnight care, split into two date entries: end the first day at 11:59 PM and start the next day at 12:00 AM.`);
            return;
          }

          // 3-hour minimum PER RANGE (not per whole booking) — matches PostJob rules.
          const rangeMinutes = endTime - startTime;
          if (rangeMinutes < 180) {
            const hrs = Math.floor(rangeMinutes / 60);
            const mins = rangeMinutes % 60;
            alert(`There is a 3-hour minimum required for all time ranges. Range ${i + 1} on ${date} is only ${hrs}h ${mins}m.`);
            return;
          }
        }
      }

      const sortedSchedule = effectiveDates
        .slice()
        .sort((a, b) => new Date(a) - new Date(b))
        .reduce((acc, date) => {
          acc[date] = formData.schedule[date];
          return acc;
        }, {});

      // IMPORTANT: only write the fields the parent actually edits. Never spread
      // the whole job object back — it carries a stale `status` captured when the
      // modal opened, so if a sitter accepted in the meantime (job -> Filled),
      // saving would overwrite it back to Open and orphan the booking.
      const updatedData = {
        title: formData.title,
        description: formData.description,
        payRange: formData.payRange,
        startDate: formData.startDate,
        endDate: formData.endDate,
        numChildren,
        childrenAges,
        selectedDates: effectiveDates,
        schedule: sortedSchedule,
      };

      const firstDate = Object.keys(sortedSchedule)[0];
      updatedData.jobStartTime = sortedSchedule[firstDate]?.start || "";

      console.log("start: " + updatedData.jobStartTime)

      try {
        const jobRef = doc(db, 'jobs', job.id);

        // Guard: never edit a job that's been accepted/booked. Re-read the live
        // state (the modal may have been open while a sitter accepted) and block
        // if it's Filled or has any engaged application. The parent must Cancel
        // instead — this is what prevents the "edited after confirmed" glitch.
        const liveJobSnap = await getDoc(jobRef);
        const liveStatus = liveJobSnap.exists() ? liveJobSnap.data().status : null;
        const engagedSnap = await getDocs(query(
          collection(db, 'applications'),
          where('parentId', '==', job.parentId),
          where('jobId', '==', job.id)
        ));
        const hasEngaged = engagedSnap.docs.some((d) =>
          ['Active', 'Awaiting Sitter Confirmation', 'Completed', 'Paid'].includes(d.data().status));
        if (liveStatus === 'Filled' || hasEngaged) {
          alert('This job has already been accepted by a sitter, so it can’t be edited. If your plans changed, use "Cancel Job" instead — or simply have the sitter log only the hours actually worked.');
          onClose();
          return;
        }

        await updateDoc(jobRef, updatedData);

        // 🔄 Update all applications tied to this job
        // Must include parentId filter to satisfy Firestore security rules
        const applicationsRef = collection(db, 'applications');
        const q = query(applicationsRef, where('parentId', '==', job.parentId), where('jobId', '==', job.id));
        const querySnapshot = await getDocs(q);

        // Only update applications whose status is NOT Paid or Refunded.
        // The Firestore rule (rightly) blocks participant-updates on terminal
        // financial states — trying to touch them would throw permission-denied
        // partway through the loop and leave the job doc updated but the
        // application docs in a mixed state.
        const editableApps = querySnapshot.docs.filter((docSnap) => {
          const s = docSnap.data().status;
          return s !== 'Paid' && s !== 'Refunded';
        });
        const updates = editableApps.map(async (docSnap) => {
          const appRef = doc(db, 'applications', docSnap.id);
          return updateDoc(appRef, {
            jobTitle: updatedData.title,
            jobDescription: updatedData.description,
            jobPayRange: updatedData.payRange,
            jobStartDate: updatedData.startDate,
            jobEndDate: updatedData.endDate,
            jobNumChildren: updatedData.numChildren,
            jobChildrenAges: updatedData.childrenAges,
            jobSchedule: updatedData.schedule,
            jobStartTime: updatedData.jobStartTime,
          });
        });

        await Promise.all(updates);

        alert('Job updated successfully!');
        onSave(); // Callback to refresh the job list
        onClose();
      } catch (error) {
        console.error('Error updating job:', error);
        alert('Failed to update job. Please try again.');
      }
    };
  
    const orderedDays = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
          <div className="p-6 flex-shrink-0">
            <h3 className="text-2xl font-bold mb-4">Edit Job</h3>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6 pt-0" style={{ WebkitOverflowScrolling: 'touch' }}>
  
          {/* Job Title */}
          <div>
            <label htmlFor="title" className="block text-lg font-medium text-gray-700">
              Job Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Job Title"
              className="w-full p-2 border rounded mb-4"
            />
          </div>
  
          {/* Job Description */}
          <div>
            <label htmlFor="description" className="block text-lg font-medium text-gray-700">
              Job Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Job Description"
              className="w-full p-2 border rounded mb-4"
              rows="4"
            />
          </div>
  
          {/* Pay Range */}
          <div>
            <label htmlFor="payRange" className="block text-lg font-medium text-gray-700">
              Pay Per Hour
            </label>
            <input
              type="number"
              value={formData.payRange}
              onChange={(e) => handleInputChange('payRange', e.target.value)}
              onBlur={(e) => {
                // Format to 2 decimal places when user leaves the field
                const value = e.target.value;
                if (value && !isNaN(parseFloat(value))) {
                  handleInputChange('payRange', parseFloat(value).toFixed(2));
                }
              }}
              placeholder="Pay Range"
              step="0.01" // Allows up to 2 decimal places
              min="0.01" // Ensures only positive numbers greater than zero
              className="w-full p-2 border rounded mb-4"
            />
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Number of Children</label>
              <input
                type="number"
                step="1"
                min="0"
                value={numChildren} // fallback to saved number
                onChange={(e) => {
                  const count = Number(e.target.value);
                  setNumChildren(count);
                  setChildrenAges((prev) => {
                    const updated = [...prev];
                    if (count > updated.length) {
                      while (updated.length < count) {
                        updated.push({ value: '', unit: 'years' });
                      }
                    } else {
                      updated.length = count;
                    }
                    return updated;
                  });
                }}
                className="w-full p-2 border border-gray-300 rounded focus:ring focus:ring-blue-300"
              />
            </div>


            {childrenAges.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {childrenAges.map((child, index) => (
                  <div key={index}>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Child {index + 1} Age
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        value={child?.value || ''}
                        onChange={(e) => {
                          const updated = [...childrenAges];
                          updated[index] = {
                            ...updated[index],
                            value: e.target.value,
                          };
                          setChildrenAges(updated);
                        }}
                        placeholder="Age"
                        min="0"
                        className="w-1/2 p-2 border border-gray-300 rounded focus:ring focus:ring-blue-300"
                      />
                      <select
                        value={child?.unit || 'years'}
                        onChange={(e) => {
                          const updated = [...childrenAges];
                          updated[index] = {
                            ...updated[index],
                            unit: e.target.value,
                          };
                          setChildrenAges(updated);
                        }}
                        className="w-1/2 p-2 border border-gray-300 rounded focus:ring focus:ring-blue-300"
                      >
                        <option value="years">Years</option>
                        <option value="months">Months</option>
                        <option value="days">Days</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
  
          {/* Start and End Dates */}
          <div>
            <label htmlFor="startDate" className="block text-lg font-medium text-gray-700">
              Start Date
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              className="w-full p-2 border rounded mb-4"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-lg font-medium text-gray-700">
              End Date
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
              className="w-full p-2 border rounded mb-4"
            />
          </div>
  
          
  
          {formData.startDate && formData.endDate && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-2">Job Dates & Schedule</h4>
              
              {/* Date Selection Interface */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Select Job Dates:</span>
                  <button
                    type="button"
                    onClick={() => {
                      const dates = generateDateRange(formData.startDate, formData.endDate);
                      setSelectedDates(dates);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                  >
                    Select All Dates
                  </button>
                </div>
                
                <div className="grid grid-cols-7 gap-2 max-h-32 overflow-y-auto p-3 bg-white rounded-lg border">
                  {generateDateRange(formData.startDate, formData.endDate).map((date) => {
                    const isSelected = selectedDates.includes(date);
                    const isToday = date === new Date().toISOString().split('T')[0];
                    const isPast = new Date(date) < new Date(new Date().toISOString().split('T')[0]);
                    
                    return (
                      <button
                        key={date}
                        type="button"
                        onClick={() => {
                          if (isPast) return; // Prevent selecting past dates
                          setSelectedDates(prev => 
                            isSelected 
                              ? prev.filter(d => d !== date)
                              : [...prev, date]
                          );
                        }}
                        disabled={isPast}
                        className={`
                          p-2 text-xs rounded-lg border transition-all duration-200
                          ${isSelected 
                            ? 'bg-blue-500 text-white border-blue-500' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                          }
                          ${isToday ? 'ring-2 ring-blue-300' : ''}
                          ${isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        {new Date(date).getDate()}
                      </button>
                    );
                  })}
                </div>
                
                <div className="mt-2 text-xs text-gray-600">
                  Selected: {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''}
                  {selectedDates.length > 7 && (
                    <span className="text-orange-600 font-medium ml-2">
                      ⚠️ Maximum 7 days allowed
                    </span>
                    )}
                </div>
              </div>
              
              <h4 className="text-lg font-semibold mb-2">Schedule for Each Day</h4>
              <button
                onClick={() => {
                  const effectiveDates = getEffectiveDates();
                  if (effectiveDates.length === 0) return alert("Please select dates first.");
                  
                  const [firstDate] = effectiveDates;
                  const firstTime = formData.schedule[firstDate];
                  if (!firstTime?.start || !firstTime?.end) return alert("Please set the first day's time.");
                  
                  const newSchedule = {};
                  effectiveDates.forEach(date => {
                    newSchedule[date] = { 
                      start: firstTime?.start || '',
                      end: firstTime?.end || '',
                    };
                  });
                  setFormData((prev) => ({ ...prev, schedule: newSchedule }));
                }}
                className="mb-4 px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
              >
                Apply First Day's Time to All Selected Dates
              </button>
              {getEffectiveDates().map((date) => (
                <div key={date} className="mb-4">
                  <label className="block font-medium mb-1">{date}</label>
                  <div className="flex flex-col space-y-2">
                    {(Array.isArray(formData.schedule?.[date]) ? formData.schedule[date] : [formData.schedule[date] || { start: '', end: '' }])
                      .map((range, index) => (
                        <div key={index} className="flex flex-col sm:flex-row gap-4 items-center">
                          <input
                            type="time"
                            value={range.start}
                            onChange={(e) => {
                              const updated = [...(formData.schedule[date] || [])];
                              updated[index] = { ...updated[index], start: e.target.value };
                              setFormData((prev) => ({
                                ...prev,
                                schedule: { ...prev.schedule, [date]: updated },
                              }));
                            }}
                            className="w-full sm:w-1/2 border border-gray-300 rounded px-2 py-1"
                          />
                          <input
                            type="time"
                            value={range.end}
                            onChange={(e) => {
                              const updated = [...(formData.schedule[date] || [])];
                              updated[index] = { ...updated[index], end: e.target.value };
                              setFormData((prev) => ({
                                ...prev,
                                schedule: { ...prev.schedule, [date]: updated },
                              }));
                            }}
                            className="w-full sm:w-1/2 border border-gray-300 rounded px-2 py-1"
                          />
                          <button
                            className="text-red-500 text-sm"
                            onClick={() => {
                              const updated = [...(formData.schedule[date] || [])];
                              updated.splice(index, 1);
                              setFormData((prev) => ({
                                ...prev,
                                schedule: { ...prev.schedule, [date]: updated },
                              }));
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}

                    <button
                      className="text-blue-600 text-sm hover:underline mt-1"
                      onClick={() => {
                        const updated = [...(formData.schedule[date] || [])];
                        updated.push({ start: '', end: '' });
                        setFormData((prev) => ({
                          ...prev,
                          schedule: { ...prev.schedule, [date]: updated },
                        }));
                      }}
                    >
                      + Add Time Range
                    </button>
                  </div>
                </div>
              ))}

            </div>
          )}
          </div>
  
          {/* Sticky Footer with Buttons */}
          <div className="p-6 pt-0 flex-shrink-0">
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center sm:text-left mb-8">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-800 via-blue-800 to-indigo-800 bg-clip-text text-transparent mb-2">
            Your Jobs
          </h2>
          <p className="text-slate-600 text-lg">
            Manage your posted jobs and review applications
          </p>
        </div>
  
      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-3 justify-center sm:justify-start mb-8">
        {['Listed Jobs', 'Job Applications', 'Active Jobs', 'Hours To Confirm', 'Completed Jobs', 'Expired Listings'].map((status) => (
          <button
            key={status}
            onClick={() => handleFilterChange(status)}
            className={`relative group px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
              filter === status 
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25' 
                : 'bg-white/80 backdrop-blur-sm border border-white/40 text-slate-700 hover:bg-white hover:shadow-lg'
            }`}
          >
            {status}

            {status === 'Hours To Confirm' && toConfirmCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-pulse">
                {toConfirmCount}
              </span>
            )}

            {status === 'Job Applications' && appCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-pulse">
                {appCount}
              </span>
            )}
          </button>
        ))}
      </div>
  
              {/* Listed Jobs / Expired Listings */}
        {(filter === 'Listed Jobs' || filter === 'Expired Listings') && (
          <div className="grid gap-6">
            {listedJobs.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds).map((job) => (
              <div
                key={job.id}
                className="group bg-white/80 backdrop-blur-sm border border-white/40 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
              >
                {/* Job Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${filter === 'Expired Listings' ? 'bg-slate-400' : 'bg-green-500 animate-pulse'}`}></div>
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${filter === 'Expired Listings' ? 'bg-slate-200 text-slate-700' : 'bg-green-100 text-green-800'}`}>
                        {filter === 'Expired Listings' ? 'Expired' : 'Open'}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      Posted {new Date(job.createdAt.seconds * 1000).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Profile Picture */}
                    {job.parentProfilePictureURL && (
                      <a href={`/profile/${job.parentId}`} className="self-center lg:self-start">
                        <div className="relative group">
                          <img
                            src={job.parentProfilePictureURL}
                            alt="Profile"
                            className="w-24 h-24 lg:w-32 lg:h-32 rounded-2xl object-cover shadow-lg border-4 border-white group-hover:border-blue-200 transition-all duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                      </a>
                    )}

                    {/* Job Details */}
                    <div className="flex-1 space-y-4">
                      {/* Job Title and Pay Rate */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h3 className="text-2xl font-bold text-slate-800 mb-2">{job.title}</h3>
                          <p className="text-slate-600 leading-relaxed">{job.description}</p>
                        </div>
                        <div className="mt-3 sm:mt-0">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-semibold text-lg">
                            ${job.payRange}/hr
                          </div>
                        </div>
                      </div>

                      {/* Job Details Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Start Date</p>
                              <p className="font-semibold text-slate-800">{formatLocalDate(job.startDate)}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Start Time</p>
                              <p className="font-semibold text-slate-800">{formatTimeToAMPM(job.jobStartTime)}</p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">End Date</p>
                              <p className="font-semibold text-slate-800">{formatLocalDate(job.endDate)}</p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Children</p>
                              <p className="font-semibold text-slate-800">
                                {job.numChildren || 0} child{(job.numChildren || 0) !== 1 ? 'ren' : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Children Ages */}
                      {((job.children && job.children.length > 0) || (job.childrenAges && job.childrenAges.length > 0)) && (
                        <div className="bg-slate-50 rounded-xl p-4">
                          <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Children's Ages</h4>
                          <div className="flex flex-wrap gap-2">
                            {job.children ? (
                              job.children.map((child, index) => (
                                <span key={child.id || index} className="bg-white px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 shadow-sm">
                                  {child.name && `${child.name}: `}{formatAge(child.age)} old
                                </span>
                              ))
                            ) : (
                              job.childrenAges.map((age, index) => (
                                <span key={index} className="bg-white px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 shadow-sm">
                                  {age.value} {age.unit} old
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="pt-4 border-t border-slate-200/50">
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => {
                              setSelectedJobForTimes(job);
                              setIsTimesModalOpen(true);
                            }}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-violet-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-purple-500/25"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span>View Schedule</span>
                            </span>
                          </button>

                          <button
                            onClick={() => setActiveJob(job)}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-blue-500/25"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              {filter === 'Expired Listings' ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              )}
                              <span>{filter === 'Expired Listings' ? 'Repost Listing' : 'Edit Job'}</span>
                            </span>
                          </button>

                          <button
                            onClick={() => deleteJob(job.id)}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold rounded-xl hover:from-red-600 hover:to-pink-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-red-500/25"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              <span>Delete Job</span>
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
  
              {/* Job Applications */}
        {filter !== 'Listed Jobs' && filter !== 'Expired Listings' && (
          <div className="grid gap-6">
            {filteredApplications.map((application) => (
              <div
                key={application.id}
                className="group bg-white/80 backdrop-blur-sm border border-white/40 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
              >
                {/* Status Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50">
                  <div className="flex items-center justify-between flex-wrap gap-y-1">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        application.status === 'Active' ? 'bg-green-500 animate-pulse' :
                        application.status === 'Completed' ? 'bg-blue-500' :
                        application.status === 'Paid' ? 'bg-emerald-500' :
                        application.status === 'Awaiting Sitter Confirmation' ? 'bg-yellow-500 animate-pulse' :
                        application.status === 'Pending' ? 'bg-orange-500' :
                        'bg-slate-400'
                      }`}></div>
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                        application.status === 'Active' ? 'bg-green-100 text-green-800' :
                        application.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                        application.status === 'Paid' ? 'bg-emerald-100 text-emerald-800' :
                        application.status === 'Awaiting Sitter Confirmation' ? 'bg-yellow-100 text-yellow-800' :
                        application.status === 'Pending' ? 'bg-orange-100 text-orange-800' :
                        'bg-slate-100 text-slate-800'
                      }`}>
                        {application.status}
                      </span>
                      {STATUS_TIPS_PARENT[application.status] && (
                        <InfoTip align="left" text={STATUS_TIPS_PARENT[application.status]} />
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      Applied {new Date(application.appliedAt.seconds * 1000).toLocaleDateString()}
                    </div>
                  </div>
                  {/* 24-hour expiration notice for awaiting sitter confirmation */}
                  {application.status === 'Awaiting Sitter Confirmation' && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-yellow-800">
                          ⏳ Waiting for sitter to confirm. Offer expires in 24 hours if not accepted.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Sitter Profile Picture */}
                    {application.sitterProfilePictureURL && (
                      <a href={`/profile/${application.sitterId}`} className="self-center lg:self-start">
                        <div className="relative group">
                          <img
                            src={application.sitterProfilePictureURL}
                            alt={application.sitterName}
                            className="w-24 h-24 lg:w-32 lg:h-32 rounded-2xl object-cover shadow-lg border-4 border-white group-hover:border-blue-200 transition-all duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                      </a>
                    )}

                    {/* Application Details */}
                    <div className="flex-1 space-y-4">
                      {/* Sitter Info and Job Title */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-slate-600 mb-1">
                            <span className="font-medium">Babysitter:</span> {application.sitterName}
                          </p>
                          <h3 className="text-2xl font-bold text-slate-800 mb-2">
                            {application.jobTitle}
                          </h3>
                        </div>
                        <div className="mt-3 sm:mt-0">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-semibold text-lg">
                            ${application.jobPayRange}/hr
                          </div>
                        </div>
                      </div>

                      {/* Job Description */}
                      <p className="text-slate-600 leading-relaxed">
                        {application.jobDescription}
                      </p>

                      {/* Message */}
                      {application.message && (
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-xl">
                          <p className="text-sm text-blue-800">
                            <span className="font-semibold">Message:</span> {application.message}
                          </p>
                        </div>
                      )}

                      {/* Job Details Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Start Date</p>
                              <p className="font-semibold text-slate-800">{formatLocalDate(application.jobStartDate)}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">End Date</p>
                              <p className="font-semibold text-slate-800">{formatLocalDate(application.jobEndDate)}</p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Pay Rate</p>
                              <p className="font-semibold text-slate-800">${application.jobPayRange}/hr</p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Sitter</p>
                              <p className="font-semibold text-slate-800">{application.sitterName}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Children Ages */}
                      {application.jobChildrenAges && application.jobChildrenAges.length > 0 && (
                        <div className="bg-slate-50 rounded-xl p-4">
                          <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Children's Ages</h4>
                          <div className="flex flex-wrap gap-2">
                            {application.jobChildrenAges.map((age, index) => (
                              <span key={index} className="bg-white px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 shadow-sm">
                                {age.value} {age.unit} old
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Work Summary for Completed/Paid Jobs */}
                      {['Completed', 'Paid'].includes(application.status) && (
                        <div className="bg-slate-50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Work Summary</h4>
                            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                              {application.totalHoursWorked || 0} hours
                            </span>
                          </div>
                          
                          {application.workLogs?.length ? (
                            <div className="space-y-2">
                              {application.workLogs.map((log, index) => (
                                <div key={index} className="bg-white p-3 rounded-lg border border-slate-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium text-slate-800">{log.date}</span>
                                  </div>
                                  <div className="space-y-1">
                                    {(Array.isArray(log.timeRanges) ? log.timeRanges : [{
                                      clockIn: log.clockIn,
                                      clockOut: log.clockOut,
                                      hoursWorked: log.hoursWorked,
                                    }]).map((range, i) => (
                                      <div key={i} className="flex items-center justify-between text-sm">
                                        <span className="text-slate-600">
                                          {formatTimeToAMPM(range.clockIn)} - {formatTimeToAMPM(range.clockOut)}
                                        </span>
                                        <span className="font-medium text-slate-800">
                                          {range.hoursWorked || 0} hours
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-500 text-sm italic">No work log available.</p>
                          )}
                        </div>
                      )}

                      {/* Payment Information for Paid Jobs */}
                      {application.status === 'Paid' && (
                        <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm text-emerald-700 font-semibold">Payment Completed</p>
                              <p className="text-lg font-bold text-emerald-800">${application.amountPaid}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="pt-4 border-t border-slate-200/50">
                        <div className="flex flex-wrap gap-3">
                          {/* Accept/Deny Buttons for Pending Applications */}
                          {application.status === 'Pending' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApplicationAction(application.id, 'accept')}
                                style={{ touchAction: 'manipulation' }}
                                className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-600 sm:transform sm:hover:scale-105 transition-all duration-200 shadow-lg shadow-green-500/25 cursor-pointer"
                              >
                                <span className="flex items-center justify-center space-x-2 pointer-events-none">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>Accept</span>
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleApplicationAction(application.id, 'deny')}
                                style={{ touchAction: 'manipulation' }}
                                className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold rounded-xl hover:from-red-600 hover:to-pink-600 sm:transform sm:hover:scale-105 transition-all duration-200 shadow-lg shadow-red-500/25 cursor-pointer"
                              >
                                <span className="flex items-center justify-center space-x-2 pointer-events-none">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  <span>Deny</span>
                                </span>
                              </button>
                            </>
                          )}

                          {/* Confirm Button for Completed Jobs */}
                          {application.status === 'Completed' && (
                            <button
                              onClick={() => handleConfirmJob(application)}
                              className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-green-500/25"
                            >
                              <span className="flex items-center justify-center space-x-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>Confirm Job</span>
                              </span>
                            </button>
                          )}

                          {/* View Schedule Button */}
                          {(application.status === 'Pending' || application.status === 'Awaiting Sitter Confirmation' || application.status === 'Active') && (
                            <button
                              onClick={() => {
                                setSelectedApplicationForTimes(application);
                                setIsTimesModalOpen(true);
                              }}
                              className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-violet-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-purple-500/25"
                            >
                              <span className="flex items-center justify-center space-x-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>View Schedule</span>
                              </span>
                            </button>
                          )}

                          {/* Message Sitter Button */}
                          {(application.status === 'Active' || (application.status === 'Completed')) && (
                            <button
                              onClick={() => handleModalOpen('chat', application.id)}
                              className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-cyan-500/25"
                            >
                              <span className="flex items-center justify-center space-x-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                <span>{application.status === 'Active' ? 'Message Sitter' : 'Message About Payment'}</span>
                              </span>
                            </button>
                          )}

                          {/* View Sitter Profile Button */}
                          <a
                            href={`/profile/${application.sitterId}`}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-blue-500/25 text-center"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span>View Sitter Profile</span>
                            </span>
                          </a>

                          {/* Cancel Job Button */}
                          {['Active', 'Awaiting Sitter Confirmation'].includes(application.status) && (
                            <button
                              onClick={() => handleCancelJob(application)}
                              disabled={cancellingIds.has(application.id)}
                              className={`flex-1 sm:flex-none px-6 py-3 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg ${cancellingIds.has(application.id) ? 'bg-gradient-to-r from-red-300 to-pink-300 cursor-not-allowed' : 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 transform hover:scale-105 shadow-red-500/25'}`}
                            >
                              <span className="flex items-center justify-center space-x-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                <span>Cancel Job</span>
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
  
      {/* Modals */}
      {activeJob && (
        <JobEditModal 
          job={activeJob} 
          onClose={() => setActiveJob(null)} 
          onSave={() => {
            fetchJobsPostedByUser().then(jobs => {
              setListedJobs(jobs);
              setActiveJob(null);
            });
          }}
        />
      )}
  
      {modalType === 'chat' && (
        <Chat applicationId={activeApplicationId} onClose={handleModalClose} />
      )}
  
      {modalType === 'review' && (
        <ReviewModal
          sitterId={activeSitterId}
          onClose={() => {
            setModalType(null);
            setActiveSitterId(null);
            setActiveApplicationId(null);
          }}
          onSubmit={handleSubmitReview}
        />
      )}
  
      {paymentApplication && (
        <PaymentModal
          application={paymentApplication}
          onClose={() => setPaymentApplication(null)}
          onConfirmPayment={handlePaymentSuccess}
          userData={currentUser}
        />
      )}
  
      <JobTimesModal
        isOpen={isTimesModalOpen}
        onClose={() => setIsTimesModalOpen(false)}
        job={selectedJobForTimes}
      />
  
      <ApplicationTimesModal
        isOpen={isTimesModalOpen}
        onClose={() => setIsTimesModalOpen(false)}
        application={selectedApplicationForTimes}
      />
      </div>
    </div>
  );
  
  
};

export default ParentJobs;
